// src/lib/kv-client.types.ts
// Helper types

export type KVValue<T> = T | null | undefined;

// Type guards
export function exists<T>(value: KVValue<T>): value is T {
  return value !== null && value !== undefined;
}

export interface BaseRangeOptions {
  withScores?: boolean;
  rev?: boolean;
  forceRefresh?: boolean;
}

export interface ZRangeOptions extends BaseRangeOptions {
  method: "rank" | "score" | "lex";
}

// Type guard to check if valid zrange options in unified kv client
export function isValidZRangeByRank(options: ZRangeOptions) {
  return (
    typeof options === "object" &&
    options !== null &&
    "method" in options &&
    options.method === "rank"
  );
}

export function isValidZRangeByScore(options: ZRangeOptions): boolean {
  return (
    typeof options === "object" &&
    options !== null &&
    "method" in options &&
    options.method === "score"
  );
}

export function isValidZRangeByLex(options: ZRangeOptions): boolean {
  return (
    typeof options === "object" &&
    options !== null &&
    "method" in options &&
    options.method === "lex"
  );
}

export type VercelRangeByScoreOptions = BaseRangeOptions & {
  byScore: true;
  byLex?: never;
};

export type VercelLexOptions = BaseRangeOptions & {
  byLex: true;
  byScore?: never;
};

/**
 * Represents an item in the key-value store
 */
export interface KVStoreItem {
  key: string;
  value: unknown;
  timestamp: number;
}

/**
 * Represents an item in a sorted set (ZSet)
 */
export interface ZSetItem {
  id?: number;
  setKey: string;
  memberKey: string;
  member: unknown;
  score: number;
  timestamp: number;
}

/**
 * Status of a sync queue item
 */
export type SyncStatus = "pending" | "processing" | "failed";

/**
 * Type of operation that can be performed
 */
export type KVOperationType = "set" | "del";
export type ZSetOperationType = "zadd" | "zrem" | "zremrangebyscore";
export type OperationType = KVOperationType | ZSetOperationType;

/**
 * Represents an item in the sync queue
 */
export interface ZSetQueuePayload {
  score?: number;
  member?: unknown;
  min?: number;
  max?: number;
}

export interface ZSetResponse<T> {
  member: T;
  score?: number;
}

export type KVQueuePayload = {
  value: unknown;
} | null;

// Type guard to check if the payload is a ZSetQueuePayload
export function isZSetQueuePayload(
  operation: string,
  payload: ZSetQueuePayload | KVQueuePayload,
): payload is ZSetQueuePayload {
  if (typeof payload !== "object" || payload === null) {
    return false;
  }

  switch (operation) {
    case "zadd":
      return "score" in payload && "member" in payload;
    case "zrem":
      return "member" in payload;
    case "zremrangebyscore":
      return "min" in payload && "max" in payload;
    default:
      return false;
  }
}

// Type guard to check if the payload is a KVQueuePayload
export function isKVQueuePayload(
  operation: string,
  payload: ZSetQueuePayload | KVQueuePayload | null,
): payload is KVQueuePayload {
  // For del operations, null payload is valid
  if (operation === "del") {
    return payload === null;
  }

  // For set operations, require object with value property
  return typeof payload === "object" && payload !== null && "value" in payload;
}

export type QueuePayload = ZSetQueuePayload | KVQueuePayload;

export interface SyncQueueItem {
  id?: number;
  operation: OperationType;
  key: string;
  payload: QueuePayload;
  timestamp: number;
  status: SyncStatus;
  retryCount: number;
}

/**
 * Options for get operations
 */
export interface GetOptions<T> {
  forceRefresh?: boolean;
  backgroundRefresh?: boolean;
  validate?: (value: unknown) => boolean;
  transform?: (value: unknown) => T;
}

export type SyncAction =
  | "UPDATED"
  | "CREATED"
  | "DELETED"
  | "MEMBER_ADDED"
  | "MEMBER_REMOVED"
  | "SET_UPDATED";

/**
 * Entity types for sync metadata
 * Used to categorize different types of data being synced
 */
export type SyncEntityType =
  | "WORKOUT_COMPLETED" // Completed workout sessions
  | "WORKOUT_PLANNED" // Planned workouts assigned to athletes
  | "ATHLETE_TEMPLATE" // Athlete-specific workout templates
  | "ATHLETE_VIEW" // Workout views created from templates
  | "EXERCISE" // Exercise data and history
  | "NOTE" // Training notes
  | "COACH_PROFILE" // Coach profile data
  | "ATHLETE_PROFILE" // Athlete profile data
  | "COACH_ATHLETE_LIST" // Coach's athlete list changes
  | "ATHLETE_PROPERTY" // Athlete custom property values
  | "ATHLETE_USER_LINK"; // User id link for athlete

/**
 * Legacy type - kept for backward compatibility
 * @deprecated Use SyncEntityType instead
 */
export type SyncMetaDataTypes = "ATHLETE" | "COACH";

/**
 * Metadata for sync operations
 * Includes information about what entity is being synced and who modified it
 */
export interface SyncMetadata {
  /**
   * Type of entity being synced
   */
  type: SyncEntityType;

  /**
   * Unique identifier for the entity
   * Format: {entityType}-{resourceId} or {entityType}-{userId}-{resourceId}
   * Examples:
   * - "WORKOUT_COMPLETED-user123-1699564800000"
   * - "ATHLETE_TEMPLATE-template-abc123"
   * - "NOTE-user123-note-xyz789"
   */
  entityId: string;

  /**
   * User ID that owns this data
   * For athlete data: athleteId
   * For coach data: coachId
   */
  userId: string;

  /**
   * User ID of who performed the action
   * Could be the athlete themselves or their coach
   */
  createdBy: string;
}

/**
 * Sync event stored in the events sorted set
 * Represents a change that needs to be synced across devices
 */
export interface SyncEvent {
  /**
   * Type of entity (from SyncEntityType)
   */
  type: string;

  /**
   * Unique identifier for the entity
   */
  entityId: string;

  /**
   * Action performed on the entity
   */
  action: SyncAction;

  /**
   * Full KV key that was modified
   */
  dataKey: string;

  /**
   * How to fetch this key during sync
   */
  fetchOp: "get" | "zrange";

  /**
   * Device ID that made the change (null if unknown)
   */
  deviceId: string | null;

  /**
   * User ID that owns the data
   */
  userId: string;

  /**
   * User ID of who performed the action
   */
  createdBy: string;
}

/**
 * Represents a batch operation
 */
export type BatchOperation =
  | { type: "set"; key: string; value: unknown; syncMetadata?: SyncMetadata }
  | { type: "del"; key: string; syncMetadata?: SyncMetadata }
  | {
      type: "zadd";
      key: string;
      score: number;
      member: string;
      syncMetadata?: SyncMetadata;
    }
  | { type: "zrem"; key: string; member: string; syncMetadata?: SyncMetadata };

/**
 * Configuration for the KV client
 */
export interface KVClientConfig {
  url?: string;
  token?: string;
  useMock?: boolean;
}

/**
 * Status of the KV client
 */
export interface KVClientStatus {
  mode: "mock" | "vercel";
  isOnline: boolean;
  syncInProgress: boolean;
  pendingOperations: SyncQueueItem[];
  queueSize: number;
}

/**
 * Basic remote client interface that both mock and real clients must implement
 */
export interface RemoteClient {
  get<T>(key: string): Promise<KVValue<T>>;

  set(key: string, value: unknown): Promise<void>;

  del(key: string): Promise<void>;

  scan(
    cursor: number,
    options?: { match?: string; count?: number },
  ): Promise<[number, string[]]>;

  zadd(key: string, data: { score: number; member: unknown }): Promise<void>;

  zrem(key: string, member: unknown): Promise<void>;

  zrange<T>(
    key: string,
    start: number,
    end: number,
    options?: BaseRangeOptions,
  ): Promise<ZSetResponse<T>[]>;

  zrangeByScore<T>(
    key: string,
    start: number | "-inf" | "+inf" | `(${number}`,
    end: number | "-inf" | "+inf" | `(${number}`,
    options?: BaseRangeOptions,
  ): Promise<ZSetResponse<T>[]>;

  zrangeByLex<T>(
    key: string,
    start: `(${string}` | `[${string}` | "-" | "+",
    end: `(${string}` | `[${string}` | "-" | "+",
    options?: BaseRangeOptions,
  ): Promise<ZSetResponse<T>[]>;

  zremrangebyscore(key: string, min: number, max: number): Promise<void>;
}
